/**
 * Zoning and STR (Short Term Rental) utilities
 *
 * Zoning Categories:
 * - Residential (R-1, R-2, R-3): No short term rentals
 * - Apartment (A-1, A-2): Long-term rentals only
 * - Apartment* (A-1, A-2 + Minatoya): STR allowed, expires 2029-2031
 * - Hotel (H, H-1, H-2, H-M): STR allowed
 */
// Zoning colors matching v1
export const ZONING_COLORS = {
    'Residential': '#34d399', // Green - no STR
    'Apartment': '#8b5cf6', // Purple - long-term only
    'Apartment*': '#f59e0b', // Amber - Minatoya (expiring STR)
    'Hotel': '#3b82f6', // Blue - STR allowed
    'default': '#9ca3af', // Gray - unknown
};
/**
 * Minatoya List - Apartment-zoned properties grandfathered for STR
 * Only applies to Maui (TMK prefix "2")
 * STR rights expire 2029-2031
 *
 * Source: https://mauipropertyexperts.com/files/MinatoyaList.pdf
 */
export const MINATOYA_LIST = {
    // Hana
    "Hana Kai-Maui": { zoning: "A-1", address: "4865 Uakea Rd", units: 20 },
    // Wailea
    "Wailea Ekahi": { zoning: "A-1", address: "3300 Wailea Alanui Dr", units: 296 },
    "Wailea Ekahi I": { zoning: "A-1", address: "3300 Wailea Alanui Dr", units: 100 },
    "Wailea Ekahi II": { zoning: "A-1", address: "3300 Wailea Alanui Dr", units: 92 },
    "Wailea Ekahi III": { zoning: "A-1", address: "3300 Wailea Alanui Dr", units: 104 },
    "Wailea Ekolu": { zoning: "A-1", address: "10 Wailea Ekolu Pl", units: 148 },
    // Only Phase 1 ("Palms at Wailea" / "Palms at Wailea I", same 152-unit
    // complex at the same address) is on the list - Phase 2 is a separate,
    // later-built complex next door and is deliberately NOT included here.
    // Confirmed live 2026-09: "PALMS AT WAILEA PH-2" had incorrectly inherited
    // this entry's Minatoya/STR status via getMinatoyaEntry's old loose
    // substring match ("...ph-2".includes("palms at wailea")) - see that
    // function's comment for the fix.
    "Palms at Wailea": { zoning: "A-1", address: "3200 Wailea Alanui Dr", units: 152 },
    "Palms at Wailea I": { zoning: "A-1", address: "3200 Wailea Alanui Dr", units: 152 },
    "Grand Champion Villas": { zoning: "A-2", address: "155 Wailea Ike Pl", units: 188 },
    "Grand Champions": { zoning: "A-2", address: "155 Wailea Ike Pl", units: 188 },
    "Grand Champions Villas": { zoning: "A-2", address: "155 Wailea Ike Pl", units: 188 },
    // Maalaea
    "Kuau Plaza": { zoning: "A-2", address: "777 Hana Hwy", units: 59 },
    "Makani A Kai": { zoning: "A-1", address: "300 Hauoli St", units: 24 },
    "Hono Kai": { zoning: "A-2", address: "280 Hauoli St", units: 46 },
    "Kanai A Nalu": { zoning: "A-2", address: "250 Hauoli St", units: 80 },
    "Maalaea Banyans": { zoning: "A-2", address: "190 Hauoli St", units: 78 },
    "Island Sands": { zoning: "A-2", address: "150 Hauoli St", units: 83 },
    "Lauloa Maalaea": { zoning: "A-2", address: "100 Hauoli St", units: 47 },
    "Maalaea Kai": { zoning: "A-2", address: "70 Hauoli St", units: 79 },
    "Milowai": { zoning: "A-2", address: "50 Hauoli St", units: 43 },
    "Milowai-Maalaea": { zoning: "A-2", address: "50 Hauoli St", units: 43 },
    // Kihei - North
    "Maui Sunset": { zoning: "A-2", address: "1032 S Kihei Rd", units: 225 },
    "Maui Schooner": { zoning: "A-2", address: "980 S Kihei Rd", units: 58 },
    "Luana Kai": { zoning: "A-2", address: "940 S Kihei Rd", units: 113 },
    "Kauhale Makai": { zoning: "A-2", address: "938 S Kihei Rd", units: 169 },
    "Kihei Bay Surf": { zoning: "A-1", address: "715 S Kihei Rd", units: 118 },
    "Leinaala": { zoning: "A-2", address: "998 S Kihei Rd", units: 24 },
    "Kihei Resort": { zoning: "A-1", address: "777 S Kihei Rd", units: 64 },
    "Kihei Bay Vista": { zoning: "A-1", address: "679 S Kihei Rd", units: 60 },
    // Kihei - Kamaole
    "Kamaole Sands": { zoning: "A-2", address: "2695 S Kihei Rd", units: 440 },
    "Maui Hill": { zoning: "A-1", address: "2881 S Kihei Rd", units: 140 },
    "Maui Kamaole": { zoning: "A-1", address: "2777 S Kihei Rd", units: 316 },
    "Maui Kamaole I": { zoning: "A-1", address: "2777 S Kihei Rd", units: 28 },
    "Maui Kamaole II": { zoning: "A-1", address: "2777 S Kihei Rd", units: 48 },
    "Maui Kamaole III": { zoning: "A-1", address: "2777 S Kihei Rd", units: 240 },
    "Hale Kamaole": { zoning: "A-1", address: "2737 S Kihei Rd", units: 188 },
    "Haleakala Shores": { zoning: "A-2", address: "2619 S Kihei Rd", units: 76 },
    "Maui Parkshore": { zoning: "A-2", address: "2653 S Kihei Rd", units: 64 },
    // Kihei - Central
    "Lihikai": { zoning: "A-1", address: "2173 Iliili Rd", units: 8 },
    "Lihikai Apts": { zoning: "A-1", address: "2173 Iliili Rd", units: 8 },
    "Lihikai Apartments": { zoning: "A-1", address: "2173 Iliili Rd", units: 8 },
    "Kamaole One": { zoning: "A-1", address: "2230 S Kihei Rd", units: 12 },
    "Punahoa Beach": { zoning: "A-1", address: "2142 Iliili Rd", units: 1 },
    "Punahoa Beach Apts": { zoning: "A-1", address: "2142 Iliili Rd", units: 1 },
    "Punahoa Beach Apartments": { zoning: "A-1", address: "2142 Iliili Rd", units: 1 },
    "Moana Villa": { zoning: "A-1", address: "1158 Uluniu Rd", units: 1 },
    "Hale Kai O Kihei": { zoning: "A-1", address: "1310 Uluniu Rd", units: 59 },
    "Hale Kai O'Kihei": { zoning: "A-1", address: "1310 Uluniu Rd", units: 59 },
    "Kihei Garden Estates": { zoning: "A-1", address: "1299 Uluniu Rd", units: 84 },
    "Waiohuli Beach Hale": { zoning: "A-1", address: "49 W Lipoa St", units: 52 },
    "Kalama Gardens": { zoning: "A-2", address: "36 Walaka St", units: 6 },
    "Kalama Terrace": { zoning: "A-2", address: "35 Walaka St", units: 61 },
    "Kalama Terrace Apartments": { zoning: "A-2", address: "35 Walaka St", units: 61 },
    "Shores of Maui": { zoning: "A-1", address: "2075 S Kihei Rd", units: 50 },
    "Pacific Shores": { zoning: "A-2", address: "2219 S Kihei Rd", units: 72 },
    "Maui Vista": { zoning: "A-2", address: "2191 S Kihei Rd", units: 280 },
    // West Maui - Kapalua
    "Kapalua Bay Villas": { zoning: "A-2", address: "500 Bay Dr", units: 141 },
    "Kapalua Golf Villas": { zoning: "A-2", address: "500 Kapalua Dr", units: 186 },
    "Kapalua Ironwoods": { zoning: "A-2", address: "Ironwood Ln", units: 39 },
    "The Ridge": { zoning: "A-2", address: "100 Ridge Road", units: 161 },
    // West Maui - Kahana
    "Kahana Reef": { zoning: "A-2", address: "4471 Lower Honoapiilani Rd", units: 88 },
    "Kahana Outrigger": { zoning: "A-1", address: "4521 Lower Honoapiilani Rd", units: 16 },
    "Kahana Village": { zoning: "A-1", address: "4531 Lower Honoapiilani Rd", units: 42 },
    "Nohonani": { zoning: "A-2", address: "3723 Lower Honoapiilani Rd", units: 28 },
    "Makani Sands": { zoning: "A-2", address: "3765 Lower Honoapiilani Rd", units: 30 },
    "Kaleialoha": { zoning: "A-2", address: "3785 Lower Honoapiilani Rd", units: 67 },
    "Lokelani": { zoning: "A-2", address: "3833 Lower Honoapiilani Rd", units: 36 },
    "Hale Mahina": { zoning: "A-2", address: "3875 Lower Honoapiilani Rd", units: 53 },
    "Hale Mahina Beach": { zoning: "A-2", address: "3875 Lower Honoapiilani Rd", units: 53 },
    "Hale Ono Loa": { zoning: "A-2", address: "3823 Lower Honoapiilani Rd", units: 67 },
    "Pikake": { zoning: "A-2", address: "3701 Lower Honoapiilani Rd", units: 12 },
    "Polynesian Shores": { zoning: "A-1", address: "3975 Lower Honoapiilani Rd", units: 52 },
    "Kuleana": { zoning: "A-1", address: "3959 Lower Honoapiilani Rd", units: 118 },
    "Hoyochi Nikko": { zoning: "A-1", address: "3901 Lower Honoapiilani Rd", units: 18 },
    "Noelani": { zoning: "A-2", address: "4095 Lower Honoapiilani Rd", units: 50 },
    // West Maui - Honokowai
    "Honokowai Palms": { zoning: "A-2", address: "3666 Lower Honoapiilani Rd", units: 30 },
    "Hale Kai": { zoning: "A-2", address: "3691 Lower Honoapiilani Rd", units: 40 },
    "Hale Kai I": { zoning: "A-2", address: "3691 Lower Honoapiilani Rd", units: 40 },
    "Paki Maui": { zoning: "A-2", address: "3615 Lower Honoapiilani Rd", units: 188 },
    "Paki Maui I": { zoning: "A-2", address: "3601 Lower Honoapiilani Rd", units: 80 },
    "Paki Maui II": { zoning: "A-2", address: "3601 Lower Honoapiilani Rd", units: 80 },
    "Paki Maui III": { zoning: "A-2", address: "3615 Lower Honoapiilani Rd", units: 108 },
    "Maui Sands": { zoning: "A-2", address: "3559 Lower Honoapiilani Rd", units: 76 },
    "Maui Sands I": { zoning: "A-2", address: "3559 Lower Honoapiilani Rd", units: 56 },
    "Maui Sands II": { zoning: "A-2", address: "3559 Lower Honoapiilani Rd", units: 20 },
    "Papakea": { zoning: "A-2", address: "3543 Lower Honoapiilani Rd", units: 364 },
    // West Maui - Kaanapali
    "Hale Kaanapali": { zoning: "A-2", address: "45 Kai Ala Dr", units: 264 },
    "Maui Eldorado": { zoning: "A-2", address: "2661 Kekaa Dr", units: 205 },
    "Kaanapali Royal": { zoning: "A-2", address: "2560 Kekaa Dr", units: 105 },
    // West Maui - Lahaina
    "Lahaina Roads": { zoning: "A-2", address: "1403 Front St", units: 42 },
    "Spinnaker": { zoning: "A-1", address: "760 Wainee St", units: 56 },
    // Molokai
    "Kenani Kai": { zoning: "A-1", address: "50 Kepuhi Pl", units: 120 },
    "Wavecrest": { zoning: "A-2", address: "7142 Kamehameha V Hwy", units: 127 },
};
// Strips everything but letters/digits, for comparing what's left of a name
// after removing a matched list entry - see getMinatoyaEntry.
function alnumOnly(s) {
    return s.replace(/[^a-z0-9]/gi, '');
}
/**
 * Check if a neighborhood is on the Minatoya list
 * Only checks for Maui TMKs (prefix "2")
 */
export function getMinatoyaEntry(neighborhoodName, tmkPrefix) {
    // Only check for Maui (TMK prefix "2") if prefix is provided
    if (tmkPrefix && !tmkPrefix.startsWith('2')) {
        return null;
    }
    if (!neighborhoodName)
        return null;
    const nameLower = neighborhoodName.toLowerCase();
    // Try exact match first
    for (const [name, data] of Object.entries(MINATOYA_LIST)) {
        if (name.toLowerCase() === nameLower) {
            return { name, ...data };
        }
    }
    // Partial match, but bounded to ONE direction: the DB name is the entry
    // plus a purely non-alphanumeric suffix (e.g. trailing punctuation) - real
    // distinguishing text (a phase number, "Ph-2", a building letter) blocks
    // the match. Known legitimate wording variants ("Apartments" vs "Apts",
    // etc.) are added to MINATOYA_LIST as explicit aliases instead of being
    // bridged here.
    //
    // The other direction - "list entry contains the DB name" (a bare
    // `entryLower.includes(nameLower)`) - used to also be checked here and is
    // deliberately gone: it matches whenever the DB name happens to be a
    // substring of some canonical entry, with zero protection against generic
    // or areawide names. Confirmed live 2026-09: "Palms at Wailea Ph-2"
    // incorrectly inherited "Palms at Wailea"'s (Phase 1 only) Minatoya status
    // this way, and a later bulk zoning-population run caught it doing the
    // same thing to at least "Kaanapali" -> "Hale Kaanapali", "Kuau" ->
    // "Kuau Plaza", and "Maalaea" -> "Maalaea Banyans" - broad area/place
    // names silently inheriting one specific building's STR-legality status.
    // Minatoya status has real short-term-rental-legality consequences for an
    // owner, so a false positive here is actively harmful, not just cosmetic.
    for (const [name, data] of Object.entries(MINATOYA_LIST)) {
        const entryLower = name.toLowerCase();
        // DB name starts with the list entry, with nothing but punctuation/whitespace left over.
        if (nameLower.startsWith(entryLower) && alnumOnly(nameLower.slice(entryLower.length)) === '') {
            return { name, ...data };
        }
    }
    return null;
}
/**
 * Check if a neighborhood is on the Minatoya list
 */
export function isOnMinatoyaList(neighborhoodName, tmkPrefix) {
    return getMinatoyaEntry(neighborhoodName, tmkPrefix) !== null;
}
/**
 * Get zoning category from a zoning code/designation
 */
export function getZoningCategory(designation, isMinatoya = false) {
    if (!designation) {
        return { category: null, color: ZONING_COLORS['default'], strAllowed: false };
    }
    const upper = designation.toUpperCase();
    // Hotel zones - STR allowed
    if (upper.startsWith('H') || upper.includes('HOTEL') || upper.includes('RESORT')) {
        return {
            category: 'Hotel',
            color: ZONING_COLORS['Hotel'],
            strAllowed: true
        };
    }
    // Apartment zones - check for Minatoya
    if (upper.startsWith('A-') || upper.includes('APARTMENT')) {
        if (isMinatoya) {
            return {
                category: 'Apartment*',
                color: ZONING_COLORS['Apartment*'],
                strAllowed: true,
                strNotes: 'Minatoya exemption expires 2029-2031'
            };
        }
        return {
            category: 'Apartment',
            color: ZONING_COLORS['Apartment'],
            strAllowed: false,
            strNotes: 'Long-term rentals only'
        };
    }
    // Residential zones - no STR
    if (upper.startsWith('R-') || upper.includes('RESIDENTIAL')) {
        return {
            category: 'Residential',
            color: ZONING_COLORS['Residential'],
            strAllowed: false,
            strNotes: 'No short-term rentals'
        };
    }
    return { category: null, color: ZONING_COLORS['default'], strAllowed: false };
}
/**
 * True for raw agricultural zoning codes ("AG", "AG-1", etc). getZoningCategory
 * doesn't classify these at all (falls through to null), and the
 * neighborhood_type auto-classifier (admin-api's populate-zoning) used to
 * silently default them to 'condo' since AG matches none of its
 * hotel/apartment/residential branches - confirmed live 2026-09 (Haiku,
 * Peahi, and ~20 others). Farm/ranch lots have no "buildings" concept, even
 * when CPR-structured (Hawaii's condominium property regime is a legal
 * subdivision mechanism, not necessarily an actual building) - used to skip
 * building-extrusion/units-UI loading for these regardless of what
 * neighborhood_type says.
 */
export function isAgriculturalZoning(zoning) {
    if (!zoning)
        return false;
    const upper = zoning.toUpperCase();
    return upper === 'AG' || upper.startsWith('AG-');
}
/**
 * Get human-readable zoning description from a map code
 */
export function getZoningDescription(mapCode) {
    if (!mapCode)
        return '';
    const upper = mapCode.toUpperCase();
    // Hotel zones
    if (upper === 'H' || upper === 'H-1' || upper === 'H-2' || upper === 'H-M' || upper.includes('HOTEL')) {
        return 'Hotel/Resort';
    }
    // Apartment zones
    if (upper === 'A-1' || upper === 'A-2' || upper.includes('APARTMENT')) {
        return 'Apartment';
    }
    // Residential zones
    if (upper === 'R-1')
        return 'Residential (Low Density)';
    if (upper === 'R-2')
        return 'Residential (Medium Density)';
    if (upper === 'R-3')
        return 'Residential (High Density)';
    if (upper.startsWith('R-'))
        return 'Residential';
    // Business/Commercial
    if (upper.startsWith('B-'))
        return 'Business/Commercial';
    // Industrial
    if (upper.includes('INDUSTRIAL'))
        return 'Industrial';
    // Agriculture
    if (upper === 'AG' || upper.startsWith('AG-'))
        return 'Agricultural';
    return mapCode; // Return as-is if unknown
}
//# sourceMappingURL=zoning.js.map